import type { ReactElement, ReactNode } from 'react';

import type { PersonQuestionModel } from '@/domain/models/personQuestionModel';
import { matchAsyncValue } from '@/presentation/stores/asyncValue';
import { usePersonQuestions } from '@/presentation/stores/usePersonQuestions';
import { IgnoreAnimatedSwitcher } from '@/presentation/widgets/IgnoreAnimatedSwitcher';

import { PersonQuestionCard } from './PersonQuestionCard';
import { useAnimatedSize } from './useAnimatedSize';
import styles from './PersonQuestionsGrid.module.css';

/**
 * Перенос `chat_page/person_questions_grid/person_questions_grid.dart`.
 *
 * `PersonQuestionWrap` с двумя фабриками (`.loading` и `.data`) свёлся
 * к одному [QuestionsWrap]: обе фабрики отличались только списком детей.
 * Поле `isShimmer` из Dart-класса не переносилось — оно там объявлено,
 * но в `build` не читается.
 */

/** `itemCount: 3` у `PersonQuestionWrap.loading`. */
const LOADING_CARD_COUNT = 3;

/** `AnimatedSize(duration: Duration(milliseconds: 400), curve: Curves.ease)`. */
const ANIMATED_SIZE_DURATION_MS = 400;

interface QuestionsWrapProps {
  readonly items: readonly ReactNode[];
}

/**
 * `SizedBox(width: double.maxFinite, child: Wrap(...))`.
 *
 * `portraitWrapper` из Dart оборачивал каждый элемент в `SizedBox` на всю
 * ширину с `Center` — но только в книжной ориентации. Здесь обёртка есть
 * всегда, а растягивается она только в книжной (см. CSS): в альбомной
 * `display: contents` убирает её из раскладки без остатка.
 */
function QuestionsWrap({ items }: QuestionsWrapProps): ReactElement {
  return (
    <div className={styles.wrap}>
      {items.map((item, index) => (
        // `List.generate` в Dart тоже нумерует детей по порядку.
        <div key={index} className={styles.item}>
          {item}
        </div>
      ))}
    </div>
  );
}

function loadingItems(): readonly ReactNode[] {
  return Array.from({ length: LOADING_CARD_COUNT }, () => (
    <PersonQuestionCard isShimmer />
  ));
}

export interface PersonQuestionsGridProps {
  readonly personId: number;
  readonly onQuestionSelect: (question: PersonQuestionModel) => void;
}

export function PersonQuestionsGrid(props: PersonQuestionsGridProps): ReactElement {
  const { personId, onQuestionSelect } = props;
  const questions = usePersonQuestions(personId);
  const { outerRef, innerRef } = useAnimatedSize({
    durationMs: ANIMATED_SIZE_DURATION_MS,
    curve: 'ease',
  });

  // `ValueKey('loading' | 'error' | 'data')` — ровно поле `status`.
  return (
    <div ref={outerRef} className={styles.animatedSize}>
      <div ref={innerRef} className={styles.inner}>
        <IgnoreAnimatedSwitcher switchKey={questions.status} className={styles.switcher}>
          {matchAsyncValue(questions, {
            loading: () => <QuestionsWrap items={loadingItems()} />,
            // Посетителю ничего не показываем — код сбоя уже в логе
            // (usePersonQuestions).
            error: () => null,
            data: (data) => (
              <QuestionsWrap
                items={data.map((question) => (
                  <PersonQuestionCard
                    question={question.question}
                    onPressed={() => onQuestionSelect(question)}
                  />
                ))}
              />
            ),
          })}
        </IgnoreAnimatedSwitcher>
      </div>
    </div>
  );
}
